/* eslint-disable no-unused-vars, no-loop-func */
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebaseFunctions/config';

export const analyticsService = {
  async getEventAnalytics(eoUserId) {
    try {
      const eventsRef = collection(db, 'events');
      const eventsQuery = query(eventsRef, where('userId', '==', eoUserId));
      const eventsSnapshot = await getDocs(eventsQuery);

      const events = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const applicationsRef = collection(db, 'mpsEventRegistration');
      const receiptsRef = collection(db, 'receipts');

      let totalApplications = 0;
      let approvedApplications = 0;
      let deniedApplications = 0;
      let pendingApplications = 0;
      let paidApplications = 0;
      let totalRevenue = 0;
      let platformFees = 0;

      const eventStats = [];
      const categoryBreakdown = {};
      const monthlyRevenue = {};
      const monthlyApplications = {};

      for (const event of events) {
        const appQuery = query(applicationsRef, where('eventId', '==', event.id));
        const appSnapshot = await getDocs(appQuery);

        let eventApps = 0;
        let eventApproved = 0;
        let eventDenied = 0;
        let eventPending = 0;
        let eventPaid = 0;
        const eventCategories = {};

        appSnapshot.docs.forEach(doc => {
          const app = doc.data();
          eventApps++;
          totalApplications++;

          if (app.applicationStatus === 'Approved' || app.applicationStatus === 'Registered') {
            eventApproved++;
            approvedApplications++;
          } else if (app.applicationStatus === 'Denied') {
            eventDenied++;
            deniedApplications++;
          } else if (app.applicationStatus === 'Applied') {
            eventPending++;
            pendingApplications++;
          }

          if (app.paymentStatus === 'Paid') {
            eventPaid++;
            paidApplications++;
          }

          if (app.businessType) {
            eventCategories[app.businessType] = (eventCategories[app.businessType] || 0) + 1;
            categoryBreakdown[app.businessType] = (categoryBreakdown[app.businessType] || 0) + 1;
          }

          const appDate = app.createdAt?.toDate ? app.createdAt.toDate() : new Date(app.createdAt);
          const monthKey = `${appDate.getFullYear()}-${String(appDate.getMonth() + 1).padStart(2, '0')}`;
          monthlyApplications[monthKey] = (monthlyApplications[monthKey] || 0) + 1;
        }); // eslint-disable-line no-loop-func

        const receiptQuery = query(receiptsRef, where('eventId', '==', event.id));
        const receiptSnapshot = await getDocs(receiptQuery);

        let eventRevenue = 0;
        let eventPlatformFees = 0;

        receiptSnapshot.docs.forEach(doc => {
          const receipt = doc.data();
          eventRevenue += receipt.eoAmount || 0;
          eventPlatformFees += receipt.platformFee || 0;
          totalRevenue += receipt.eoAmount || 0;
          platformFees += receipt.platformFee || 0;

          const receiptDate = receipt.createdAt?.toDate ? receipt.createdAt.toDate() : new Date(receipt.createdAt);
          const monthKey = `${receiptDate.getFullYear()}-${String(receiptDate.getMonth() + 1).padStart(2, '0')}`;
          monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + (receipt.eoAmount || 0);
        });

        eventStats.push({
          eventId: event.id,
          eventName: event.eventName,
          startDate: event.startDate,
          endDate: event.endDate,
          applications: eventApps,
          approved: eventApproved,
          denied: eventDenied,
          pending: eventPending,
          paid: eventPaid,
          revenue: eventRevenue,
          platformFees: eventPlatformFees,
          categories: eventCategories,
          capacity: event.sellerCapacity || 0,
          fillRate: event.sellerCapacity ? Math.round((eventApproved / event.sellerCapacity) * 100) : 0
        });
      }

      const sortedMonths = Object.keys(monthlyRevenue).sort();
      const revenueTimeline = sortedMonths.map(month => ({
        month,
        revenue: monthlyRevenue[month] || 0,
        applications: monthlyApplications[month] || 0
      }));

      const sortedCategories = Object.entries(categoryBreakdown)
        .sort((a, b) => b[1] - a[1])
        .map(([category, count]) => ({ category, count }));

      return {
        success: true,
        summary: {
          totalEvents: events.length,
          totalApplications,
          approvedApplications,
          deniedApplications,
          pendingApplications,
          paidApplications,
          totalRevenue,
          platformFees,
          approvalRate: totalApplications > 0 ? Math.round((approvedApplications / totalApplications) * 100) : 0,
          conversionRate: approvedApplications > 0 ? Math.round((paidApplications / approvedApplications) * 100) : 0
        },
        eventStats,
        categoryBreakdown: sortedCategories,
        revenueTimeline
      };
    } catch (error) {
      console.error('Error fetching event analytics:', error);
      return { success: false, message: error.message };
    }
  },

  async getApplicationPipeline(eoUserId) {
    try {
      const eventsRef = collection(db, 'events');
      const eventsQuery = query(eventsRef, where('userId', '==', eoUserId));
      const eventsSnapshot = await getDocs(eventsQuery);
      const eventIds = eventsSnapshot.docs.map(doc => doc.id);

      if (eventIds.length === 0) {
        return {
          success: true,
          pipeline: {
            applied: 0,
            reviewing: 0,
            approved: 0,
            denied: 0,
            paid: 0,
            withdrawn: 0
          }
        };
      }

      const applicationsRef = collection(db, 'mpsEventRegistration');

      const pipeline = {
        applied: 0,
        reviewing: 0,
        approved: 0,
        denied: 0,
        paid: 0,
        withdrawn: 0
      };

      for (const eventId of eventIds) {
        const appQuery = query(applicationsRef, where('eventId', '==', eventId));
        const appSnapshot = await getDocs(appQuery);

        appSnapshot.docs.forEach(doc => {
          const app = doc.data();
          const status = app.applicationStatus?.toLowerCase() || 'applied';

          if (status === 'applied') pipeline.applied++;
          else if (status === 'reviewing') pipeline.reviewing++;
          else if (status === 'approved' || status === 'registered') {
            if (app.paymentStatus === 'Paid') {
              pipeline.paid++;
            } else {
              pipeline.approved++;
            }
          }
          else if (status === 'denied') pipeline.denied++;
          else if (status === 'withdrawn') pipeline.withdrawn++;
        });
      }

      return { success: true, pipeline };
    } catch (error) {
      console.error('Error fetching application pipeline:', error);
      return { success: false, message: error.message };
    }
  },

  async getRevenueByPeriod(eoUserId, period = 'month') {
    try {
      const receiptsRef = collection(db, 'receipts');
      const receiptsQuery = query(receiptsRef, where('eoUserId', '==', eoUserId), orderBy('createdAt', 'desc'));
      const receiptsSnapshot = await getDocs(receiptsQuery);

      const periodData = {};
      const now = new Date();
      let startDate;

      if (period === 'week') {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (period === 'month') {
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      } else if (period === 'quarter') {
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      } else {
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      }

      let periodRevenue = 0;
      let periodTransactions = 0;

      receiptsSnapshot.docs.forEach(doc => {
        const receipt = doc.data();
        const receiptDate = receipt.createdAt?.toDate ? receipt.createdAt.toDate() : new Date(receipt.createdAt);

        if (receiptDate >= startDate) {
          periodRevenue += receipt.eoAmount || 0;
          periodTransactions++;

          let key;
          if (period === 'week') {
            key = receiptDate.toISOString().split('T')[0];
          } else if (period === 'month') {
            key = receiptDate.toISOString().split('T')[0];
          } else {
            key = `${receiptDate.getFullYear()}-${String(receiptDate.getMonth() + 1).padStart(2, '0')}`;
          }

          if (!periodData[key]) {
            periodData[key] = { revenue: 0, transactions: 0 };
          }
          periodData[key].revenue += receipt.eoAmount || 0;
          periodData[key].transactions++;
        }
      });

      const timeline = Object.entries(periodData)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, data]) => ({ date, ...data }));

      return {
        success: true,
        period,
        totalRevenue: periodRevenue,
        totalTransactions: periodTransactions,
        averageTransaction: periodTransactions > 0 ? Math.round(periodRevenue / periodTransactions) : 0,
        timeline
      };
    } catch (error) {
      console.error('Error fetching revenue by period:', error);
      return { success: false, message: error.message };
    }
  },

  async getYearOverYearComparison(eoUserId) {
    try {
      const receiptsRef = collection(db, 'receipts');
      const receiptsQuery = query(receiptsRef, where('eoUserId', '==', eoUserId));
      const receiptsSnapshot = await getDocs(receiptsQuery);

      const now = new Date();
      const currentYear = now.getFullYear();
      const lastYear = currentYear - 1;

      let currentYearRevenue = 0;
      let lastYearRevenue = 0;
      let currentYearTransactions = 0;
      let lastYearTransactions = 0;

      receiptsSnapshot.docs.forEach(doc => {
        const receipt = doc.data();
        const receiptDate = receipt.createdAt?.toDate ? receipt.createdAt.toDate() : new Date(receipt.createdAt);
        const year = receiptDate.getFullYear();

        if (year === currentYear) {
          currentYearRevenue += receipt.eoAmount || 0;
          currentYearTransactions++;
        } else if (year === lastYear) {
          lastYearRevenue += receipt.eoAmount || 0;
          lastYearTransactions++;
        }
      });

      const revenueGrowth = lastYearRevenue > 0
        ? Math.round(((currentYearRevenue - lastYearRevenue) / lastYearRevenue) * 100)
        : currentYearRevenue > 0 ? 100 : 0;

      const transactionGrowth = lastYearTransactions > 0
        ? Math.round(((currentYearTransactions - lastYearTransactions) / lastYearTransactions) * 100)
        : currentYearTransactions > 0 ? 100 : 0;

      return {
        success: true,
        currentYear: {
          year: currentYear,
          revenue: currentYearRevenue,
          transactions: currentYearTransactions
        },
        lastYear: {
          year: lastYear,
          revenue: lastYearRevenue,
          transactions: lastYearTransactions
        },
        growth: {
          revenue: revenueGrowth,
          transactions: transactionGrowth
        }
      };
    } catch (error) {
      console.error('Error fetching YoY comparison:', error);
      return { success: false, message: error.message };
    }
  },

  async getTopPerformingEvents(eoUserId, limit = 5) {
    try {
      const analytics = await this.getEventAnalytics(eoUserId);

      if (!analytics.success) {
        return analytics;
      }

      const sortedByRevenue = [...analytics.eventStats]
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, limit);

      const sortedByApplications = [...analytics.eventStats]
        .sort((a, b) => b.applications - a.applications)
        .slice(0, limit);

      const sortedByFillRate = [...analytics.eventStats]
        .filter(e => e.capacity > 0)
        .sort((a, b) => b.fillRate - a.fillRate)
        .slice(0, limit);

      return {
        success: true,
        byRevenue: sortedByRevenue,
        byApplications: sortedByApplications,
        byFillRate: sortedByFillRate
      };
    } catch (error) {
      console.error('Error fetching top events:', error);
      return { success: false, message: error.message };
    }
  },

  async getDashboardSummary(eoUserId) {
    try {
      const [analytics, pipeline, yoy, topEvents] = await Promise.all([
        this.getEventAnalytics(eoUserId),
        this.getApplicationPipeline(eoUserId),
        this.getYearOverYearComparison(eoUserId),
        this.getTopPerformingEvents(eoUserId, 3)
      ]);

      return {
        success: true,
        summary: analytics.success ? analytics.summary : null,
        pipeline: pipeline.success ? pipeline.pipeline : null,
        yoy: yoy.success ? yoy : null,
        topEvents: topEvents.success ? topEvents : null,
        categoryBreakdown: analytics.success ? analytics.categoryBreakdown : [],
        revenueTimeline: analytics.success ? analytics.revenueTimeline : []
      };
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      return { success: false, message: error.message };
    }
  }
};
